import { useEffect, useState } from "react";

/**
 * The shell's viewport tiers (#11742).
 *
 * Claude Design ruling (2026-08-30): SHELL behavior — whether the nav
 * reserves a column, whether viewers split or overlay, the page gutter —
 * keys on the viewport, because the viewport is the shell's own frame.
 * CONTENT surfaces (toolbars, panes) key on their container's measured
 * width instead, since the rail state changes how much width they actually
 * have (the AgentChatConversation house rule). The one sanctioned
 * exception on a content surface: a requirement the ruling itself states
 * as a viewport rule (the filters panel overlaying "below the mobile
 * breakpoint").
 *
 * phone < 1024px ≤ desktop — evaluated through the same media query
 * Tailwind's `md:` compiles to (the config moves `md` to 1024), so the JS
 * tier and the CSS breakpoint cannot disagree at fractional widths under
 * browser zoom (#11742 review N17).
 *
 * The line moved up from 768 in round 8 (Hunter's iPad pass): an iPad
 * portrait at 820px used to sit in a tablet tier with a 320px push rail
 * beside ~470px of content that got none of the phone designs built for
 * exactly that width — "the iPad dimensions just weren't enough to trigger
 * the mobile designs". The tablet tier is gone with it: its two consumers
 * (a 24px gutter step, a collapsed-rail first-load default) existed to make
 * a 768px push rail bearable, and no push rail exists below 1024 now. An
 * iPad landscape (1180) is desktop.
 */
export type ViewportTier = "phone" | "desktop";

const DESKTOP_MIN_QUERY = "(min-width: 1024px)";

/** The tier at this instant — for reads outside React (storage defaults). */
export function currentViewportTier(): ViewportTier {
  return window.matchMedia(DESKTOP_MIN_QUERY).matches ? "desktop" : "phone";
}

/** The live tier, updating as the viewport crosses the line. */
export function useViewportTier(): ViewportTier {
  const [tier, setTier] = useState<ViewportTier>(() => currentViewportTier());

  useEffect(() => {
    // A boundary query instead of a resize listener: it fires only on a
    // crossing, not on every dragged pixel.
    const desktopQuery = window.matchMedia(DESKTOP_MIN_QUERY);
    const sync = () => setTier(currentViewportTier());
    desktopQuery.addEventListener("change", sync);
    return () => desktopQuery.removeEventListener("change", sync);
  }, []);

  return tier;
}

/** True below 1024px — the structural branch for drawer/overlay chrome. */
export function useIsPhoneViewport(): boolean {
  return useViewportTier() === "phone";
}
